import React, { useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { SupportWidgetConfig } from '../types';
import { SupportWidgetContext } from '../context';
import { WidgetStoreContext, TutorialStoreContext } from '../hooks';
import { createWidgetStore } from '../stores/widgetStore';
import { createTutorialStore } from '../stores/tutorialStore';
import { buildCssVars } from '../theme';
import { SupportWidgetTrigger } from './SupportWidgetTrigger';
import { SupportWidgetPanel } from './SupportWidgetPanel';

interface SupportWidgetProps {
  config: SupportWidgetConfig;
}

/**
 * Root component for the Netrun Support Widget.
 *
 * Provides React context (config), Zustand stores (widget + tutorial),
 * and renders the trigger button, panel, and spotlight overlay
 * into a portal on document.body.
 *
 * Usage:
 * ```tsx
 * <SupportWidget config={{ productId: 'intirkast', productName: 'Intirkast', tabs: ['tutorials', 'docs'] }} />
 * ```
 */
export function SupportWidget({ config }: SupportWidgetProps) {
  const widgetStore = useMemo(
    () => createWidgetStore(config.productId, config.defaultWidth ?? 380),
    [config.productId, config.defaultWidth],
  );

  const tutorialStore = useMemo(
    () => createTutorialStore(config.productId),
    [config.productId],
  );

  // Notify parent when open/close state changes
  useEffect(() => {
    if (!config.onToggle) return;
    const unsub = widgetStore.subscribe((state, prevState) => {
      if (state.isOpen !== prevState.isOpen) {
        config.onToggle?.(state.isOpen);
      }
    });
    return unsub;
  }, [widgetStore, config.onToggle]);

  const cssVars = useMemo(() => buildCssVars(config.theme), [config.theme]);
  const container = config.portalContainer ?? document.body;

  return (
    <SupportWidgetContext.Provider value={config}>
      <WidgetStoreContext.Provider value={widgetStore}>
        <TutorialStoreContext.Provider value={tutorialStore}>
          {createPortal(
            <div
              className={config.className}
              style={cssVars as React.CSSProperties}
              data-support-widget={config.productId}
            >
              <SupportWidgetTrigger />
              <SupportWidgetPanel />
            </div>,
            container,
          )}
        </TutorialStoreContext.Provider>
      </WidgetStoreContext.Provider>
    </SupportWidgetContext.Provider>
  );
}
